import moment from 'moment';
import { cloneDeep } from 'lodash';

import { truncateTextWithEllipsis } from '@utilities';

import { surveyDatePromptIds } from '@api/surveys/utilities/constants';

import { CONCEPT_ERROR_STATE } from './constants';

const EXCLUDED_REQUIRED_PROMPT_KEYS = new Set(['comparison_period', 'purchase_shift']);
const PILL_DATE_FORMAT = 'MM/DD/YYYY';
const SAMPLE_VALIDATION_PROMPT_KEYS = new Set(['categories', 'place', 'product']);

/**
 * @typedef {Object} PromptAnswerValue value returned from Insights prompt searching
 * @property {string} attributeId eg. "sector"
 * @property {string} id eg. "sector:isc_apparel"
 * @property {boolean} is_fixed
 * @property {string} name
 * @property {string} prompt_desc
 */

/**
 * @typedef {Object} PromptAnswer
 * @property {string} [field]
 * @property {string} fieldType
 * @property {number|string} [id]
 * @property {number} promptId
 * @property {Array<PromptAnswerValue|string>} values
 */

const getAnswerValueKey = (value) => {
  if (value && typeof value === 'object') {
    if (value.id !== undefined && value.id !== null) {
      return `id:${value.id}`;
    }
    if (value.attributeId || value.field) {
      return `attr:${value.attributeId || value.field}:${value.name || ''}`;
    }
    return JSON.stringify(value);
  }
  return `${typeof value}:${String(value)}`;
};

const dedupeAnswerValues = (values = []) => {
  const seen = new Set();
  const result = [];
  (values || []).forEach((value) => {
    if (value === null || typeof value !== 'object') {
      result.push(cloneDeep(value));
      return;
    }

    const key = getAnswerValueKey(value);
    if (seen.has(key)) return;
    seen.add(key);
    result.push(cloneDeep(value));
  });
  return result;
};

export const normalizePromptAnswer = (answer = {}) => {
  const cloned = cloneDeep(answer);
  cloned.values = dedupeAnswerValues(cloned.values);
  if (cloned._delete && cloned.values.length > 0) {
    delete cloned._delete;
  }
  return cloned;
};

export const normalizePromptAnswers = (answers = []) =>
  (answers || []).map((answer) => normalizePromptAnswer(answer));

const normalizePromptKey = (key = '') => key.replace(/_\d+$/, '');

const getPromptBehaviorKey = (prompt = {}) =>
  prompt.behaviorGroup ?? prompt.promptKey?.match(/_(\d+)$/)?.[1] ?? null;

export const hasSampleValidationCriteria = ({ group = {}, surveyType = {} } = {}) => {
  const promptsById = new Map(
    [...(surveyType.prompts || []), ...(surveyType.advancedPrompts || [])].map((prompt) => [
      String(prompt.id),
      prompt,
    ])
  );
  const criteriaByBehavior = new Map();

  (group.promptAnswers || []).forEach((answer) => {
    if (answer?._delete || !answer?.values?.length) return;

    const prompt = promptsById.get(String(answer.promptId));
    if (!prompt) return;

    const behaviorKey = getPromptBehaviorKey(prompt);
    if (!behaviorKey) return;

    const current = criteriaByBehavior.get(behaviorKey) || {
      hasDate: false,
      hasSampleCriteria: false,
    };
    criteriaByBehavior.set(behaviorKey, {
      hasDate:
        current.hasDate ||
        normalizePromptKey(prompt.promptKey) === 'date_range' ||
        surveyDatePromptIds.includes(prompt.insightsKey),
      hasSampleCriteria:
        current.hasSampleCriteria ||
        SAMPLE_VALIDATION_PROMPT_KEYS.has(normalizePromptKey(prompt.promptKey)),
    });
  });

  return (
    criteriaByBehavior.size > 0 &&
    [...criteriaByBehavior.values()].every(({ hasDate, hasSampleCriteria }) => {
      return hasDate && hasSampleCriteria;
    })
  );
};

/**
 * transformPromptSelections
 * @param {Object[]|string[]|Object} options.answers
 * @param {Object} options.prompt
 * @return {PromptAnswer[]}
 */
export const transformPromptSelections = ({ answers, prompt }) => {
  if (!answers || answers.length === 0) return [];
  const isDateRange = surveyDatePromptIds.includes(prompt.insightsKey);

  if (prompt.promptKey === 'comparison_period') {
    if (answers && answers?.comparison_period) {
      return normalizePromptAnswers([
        {
          fieldType: 'COMPARISON_PERIOD',
          promptId: prompt?.id,
          values: answers.comparison_period,
        },
      ]);
    }

    return normalizePromptAnswers([
      {
        fieldType: 'COMPARISON_PERIOD',
        promptId: prompt?.id,
        values: answers,
      },
    ]);
  }

  if (isDateRange) {
    // for point forward date ranges
    if (answers.length === 1 && typeof answers[0] === 'string') {
      return normalizePromptAnswers([
        {
          field: prompt.attributes[0]?.field,
          fieldType: 'DATE',
          operator: 'GREATER_THAN_OR_EQUAL',
          promptId: prompt.id,
          values: answers,
        },
      ]);
    }

    if (answers.date_range) {
      return normalizePromptAnswers([
        {
          field: prompt.attributes[0]?.field,
          fieldType: 'DATE',
          operator: 'IN_RANGE',
          promptId: prompt.id,
          values: answers.date_range,
        },
      ]);
    }

    return normalizePromptAnswers([
      {
        field: prompt.attributes[0]?.field,
        fieldType: 'DATE',
        operator: 'IN_RANGE',
        promptId: prompt.id,
        values: answers,
      },
    ]);
  }

  const isCustomFilterPrompt =
    prompt.insightsKey === 'purchase_shift' ||
    prompt.insightsKey === 'purchase_volume' ||
    prompt.insightsKey === 'unique_brands';

  if (isCustomFilterPrompt) return normalizePromptAnswers(answers);

  const defaultField = prompt.attributes?.[0]?.field;
  const promptAnswers = [];
  answers.forEach((answer) => {
    const field =
      answer.field ||
      prompt.attributes?.find(({ id }) => answer.attributeId === id)?.field ||
      defaultField;
    const existingAnswer = promptAnswers.find(
      ({ field: existingField }) => existingField === field
    );
    if (existingAnswer) {
      existingAnswer.operator = 'IN_LIST';
      existingAnswer.values.push(answer);
    } else {
      promptAnswers.push({
        field,
        fieldType: 'TEXT',
        operator: 'EQUALS',
        promptId: prompt.id,
        values: [answer],
      });
    }
  });

  return normalizePromptAnswers(promptAnswers);
};

/**
 * gets value for pill based on current prompt and answers
 * @param {Object} options
 * @param {number} [options.firstAnswerMaxLength]
 * @param {Object} options.prompt
 * @param {PromptAnswer[]} options.promptAnswers group.promptAnswers
 * @return {string}
 */
export const getPromptPillValue = ({ firstAnswerMaxLength = 0, prompt, promptAnswers = [] }) => {
  if (prompt.promptKey === 'comparison_period') {
    if (promptAnswers?.length === 0) return prompt?.emptyValue;
    const selectedIds = promptAnswers
      .filter(({ promptId }) => promptId === prompt.id)
      .flatMap(({ values = [] }) => values);

    if (selectedIds.length === 0) return prompt?.emptyValue;

    return prompt?.dateOptions?.find((dateOption) =>
      dateOption.options.some(({ value }) => selectedIds.includes(value[0]))
    )?.timeFrame;
  }

  if (prompt?.insightsKey === 'purchase_volume' || prompt?.insightsKey === 'purchase_shift') {
    const attribute = prompt.attributes?.find(
      (attr) => attr.id === promptAnswers?.[0]?.attributeId
    );
    const name = attribute?.name;
    return name || prompt?.emptyValue;
  }
  const matchingPromptAnswers = promptAnswers.filter(
    ({ _delete, promptId }) => !_delete && promptId === prompt.id
  );
  const answers = matchingPromptAnswers.map(({ values }) => values).flat(1);

  const isDateRange = surveyDatePromptIds.includes(prompt.insightsKey);

  if (answers.length === 0) return prompt?.emptyValue;

  if (isDateRange) {
    // for point forward date ranges
    if (answers.length === 1 && typeof answers[0] === 'string') {
      return `${moment(answers[0]).format(PILL_DATE_FORMAT)} or later`;
    }

    if (answers.length === 2 && typeof answers[0] === 'string') {
      return [answers[0], answers[1]]
        .map((value) => moment(value))
        .sort((a, b) => a.diff(b)) // values not guaranteed to be in order, sort is necessary
        .map((date) => date.format(PILL_DATE_FORMAT))
        .join(' to ');
    }

    return answers[0]?.date_range
      ?.map((value) => moment(value).format(PILL_DATE_FORMAT))
      .join(' to ');
  }

  const firstAnswerAttribute = prompt?.attributes?.find(
    (attribute) => attribute?.field === answers[0].attributeId
  );

  const firstAnswerName = firstAnswerMaxLength
    ? truncateTextWithEllipsis({
        length: answers.length > 1 ? firstAnswerMaxLength : firstAnswerMaxLength + 10,
        text: answers[0].name,
      })
    : answers[0].name;

  const firstAnswer =
    firstAnswerAttribute?.name && prompt?.isPrefixIncluded && prompt?.insightsKey !== 'new_item'
      ? `${firstAnswerAttribute?.name}: ${firstAnswerName}`
      : firstAnswerName;

  if (answers.length > 1) {
    return `${firstAnswer} ${prompt.isSortEnabled ? 'then' : 'or'} ${answers.length - 1} more`;
  }
  return firstAnswer;
};

/**
 * gets tooltip content for pill based on current prompt and answers
 * @param {Object} options.prompt
 * @param {PromptAnswer[]} options.promptAnswers group.promptAnswers
 * @return {string}
 */
export const getPromptPillTooltip = ({ prompt, promptAnswers = [] }) => {
  const answers = promptAnswers
    .filter(({ _delete, promptId }) => !_delete && promptId === prompt.id)
    .map(({ values }) => values)
    .flat(1);

  const isDateRange = surveyDatePromptIds.includes(prompt?.insightsKey);
  const isPurchaseVolume = prompt?.insightsKey === 'purchase_volume';
  const isPurchaseShift = prompt?.insightsKey === 'purchase_shift';

  if (answers.length <= 1 || isDateRange || isPurchaseVolume || isPurchaseShift) return null;

  return answers.map((value, index) => {
    return (
      <div key={`selection-${index}`}>
        <small>
          {getPromptPillValue({
            prompt,
            promptAnswers: [{ promptId: prompt.id, values: [value] }],
          })}
        </small>
      </div>
    );
  });
};

/**
 * Returns the CONCEPT_ERROR_STATE key that blocks Continue, or null when concepts are valid.
 * @param {Object[]} groups - activeGroups from SurveyContext
 * @param {boolean} isConceptImageTypeTest
 * @returns {string|null}
 */
export const getConceptErrorState = (groups, isConceptImageTypeTest) => {
  if (!isConceptImageTypeTest) return null;

  const groupStates = groups.map(({ conceptImage, conceptName }) => ({
    hasImage: Boolean(conceptImage),
    hasName: Boolean(conceptName),
  }));

  const someMissingBoth = groupStates.some(({ hasImage, hasName }) => !hasImage && !hasName);
  const someHaveImage = groupStates.some(({ hasImage }) => hasImage);
  const allHaveImage = groupStates.every(({ hasImage }) => hasImage);
  const someHaveName = groupStates.some(({ hasName }) => hasName);
  const allHaveName = groupStates.every(({ hasName }) => hasName);

  if (someMissingBoth) return CONCEPT_ERROR_STATE.MISSING_EITHER;
  if (someHaveImage && !allHaveImage) return CONCEPT_ERROR_STATE.INCONSISTENT_IMAGE;
  if (someHaveName && !allHaveName) return CONCEPT_ERROR_STATE.INCONSISTENT_NAME;

  return null;
};

/**
 * Pure evaluator for the Panelists Continue button disabled conditions.
 * @param {Object} params
 * @param {Object[]} params.activeGroups
 * @param {boolean} params.isResearchObjectivesTooLong
 * @param {Object[]} params.samplePricing - insightsDocumentType.samplePricing
 * @returns {boolean}
 */
export const isPanelistsContinueDisabled = ({
  activeGroups,
  isResearchObjectivesTooLong,
  samplePricing,
}) => {
  const currentTotalResponses = activeGroups.reduce((acc, group) => acc + group.quotaLimit, 0);
  const minimumResponsesRequired = Math.min(
    ...samplePricing
      .filter(({ quotaGroups }) => quotaGroups === activeGroups.length)
      .map(({ responses }) => responses)
  );

  return (
    currentTotalResponses < minimumResponsesRequired ||
    activeGroups.some(({ name }) => !name.trim()) ||
    new Set(activeGroups.map(({ name }) => name.trim())).size !== activeGroups.length ||
    isResearchObjectivesTooLong
  );
};

const hasPromptAnswer = (promptAnswers, promptId) =>
  promptAnswers.some(
    ({ _delete, promptId: answerPromptId, values }) =>
      !_delete && answerPromptId === promptId && values?.length > 0
  );

const isRequiredPromptMissing = ({ prompt, promptAnswers }) => {
  if (!prompt.isRequired) return false;

  const answers = promptAnswers.filter(
    ({ _delete, promptId }) => !_delete && promptId === prompt.id
  );

  return answers.length === 0 || answers.some(({ values }) => !values?.length);
};

/**
 * Pure evaluator for required-prompt completeness. Accepts the visible-prompts Map from
 * Panelists local state so that the evaluation logic is reusable without moving UI state.
 * @param {Object} params
 * @param {Object[]} params.groups - activeGroups
 * @param {Object[]} params.prompts - instantSurveyType.prompts
 * @param {Map<string|number, Set<number>>} params.visiblePromptsByGroup
 * @returns {Array<{clientId, id, name, prompts: Object[]}>}
 */
export const getGroupsWithMissingRequiredPrompts = ({ groups, prompts, visiblePromptsByGroup }) =>
  groups
    .map(({ clientId, id, name, promptAnswers }) => {
      const groupKey = clientId || id;
      const visiblePromptIds = visiblePromptsByGroup.get(groupKey) || new Set();
      return {
        clientId,
        id,
        name,
        prompts: prompts.filter((prompt) => {
          if (!visiblePromptIds.has(prompt.id)) return false;
          if (!prompt.isRequired) return false;

          return isRequiredPromptMissing({
            prompt,
            promptAnswers,
          });
        }),
      };
    })
    .filter(({ prompts }) => prompts.length > 0);

const getBehaviorPromptGroups = (prompts) => {
  const promptsByBehaviorGroup = prompts
    .filter(({ promptKey }) => promptKey && !EXCLUDED_REQUIRED_PROMPT_KEYS.has(promptKey))
    .reduce((acc, prompt) => {
      if (!acc.has(prompt.behaviorGroup)) {
        acc.set(prompt.behaviorGroup, []);
      }

      acc.get(prompt.behaviorGroup).push(prompt);
      return acc;
    }, new Map());

  return [...promptsByBehaviorGroup.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, behaviorGroupPrompts]) => behaviorGroupPrompts);
};

/**
 * Returns true when persisted Panelists data is missing a required prompt.
 *
 * Behavior group 1 is always in scope. Additional behavior groups become
 * in scope once a prompt in that group has a persisted, non-empty answer.
 *
 * This intentionally does not account for behavior groups that were expanded
 * in the UI but left completely unanswered.
 * @param {Object} params
 * @param {Object[]} params.groups - activeGroups
 * @param {Object[]} params.prompts - instantSurveyType.prompts
 * @returns {boolean}
 */
export const hasGroupsWithMissingRequiredPrompts = ({ groups, prompts }) => {
  const behaviorPromptGroups = getBehaviorPromptGroups(prompts);

  if (behaviorPromptGroups.length === 0) return false;

  return groups.some(({ promptAnswers = [] }) => {
    let visibleBehaviorGroupCount = 1;

    behaviorPromptGroups.forEach((behaviorGroupPrompts, index) => {
      const hasAnswerInBehaviorGroup = behaviorGroupPrompts.some(({ id }) =>
        hasPromptAnswer(promptAnswers, id)
      );

      if (hasAnswerInBehaviorGroup) {
        visibleBehaviorGroupCount = Math.max(visibleBehaviorGroupCount, index + 1);
      }
    });

    const inScopePrompts = behaviorPromptGroups.slice(0, visibleBehaviorGroupCount).flat();

    return inScopePrompts.some((prompt) => isRequiredPromptMissing({ prompt, promptAnswers }));
  });
};
